// ====================================================================================================
//  File Name   : ReleaseManagerPage.js
//  Location    : src/pages/admin/ReleaseManagerPage.js
//  Project     : AIRS Security - Autonomous Sentinel Ecosystem
//  Developed By: Appence Software Solution | https://appence.com
//  Legal Notice: Proprietary Property. Unauthorized use strictly prohibited. (c) 2026
// ====================================================================================================

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { Rocket, History, Package, AlertTriangle, Loader2, Monitor, Terminal, Upload } from 'lucide-react';

const ReleaseManagerPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('linux'); // 'linux' | 'windows'
  const [version, setVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [exeFile, setExeFile] = useState(null);

  // Fetch History Filtered by Active Platform
  const { data: history = [] } = useQuery({
    queryKey: ['changelog', activeTab],
    queryFn: async () => {
      const res = await axiosClient.get(`/public/changelog?platform=${activeTab}`);
      return res.data || res;
    }
  });

  // Linux Build Mutation (UNTOUCHED - Existing Endpoint)
  const linuxBuildMutation = useMutation({
    mutationFn: (data) => axiosClient.post('/admin/agent/build', data),
    onSuccess: (res) => {
        alert(res.message || "Linux Build & Release Successful!");
        setVersion('');
        setNotes('');
        queryClient.invalidateQueries(['changelog', 'linux']);
    },
    onError: (err) => {
        alert("Linux Build Failed: " + (err.response?.data?.detail || err.message));
    }
  });

  // Windows Release Mutation (NEW - File Upload Endpoint)
  const windowsReleaseMutation = useMutation({
    mutationFn: (formData) => axiosClient.post('/admin/releases/windows', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    onSuccess: (res) => {
        alert(res.message || "Windows Release Published Successfully!");
        setVersion('');
        setNotes('');
        setExeFile(null);
        queryClient.invalidateQueries(['changelog', 'windows']);
    },
    onError: (err) => {
        alert("Windows Release Failed: " + (err.response?.data?.detail || err.message));
    }
  });

  const handleLinuxRelease = (e) => {
      e.preventDefault();
      if(!version || !notes) return alert("Please fill all fields");
      
      const confirmMsg = `
            Are you sure you want to release Linux ${version}?

            1. Ensure you have compiled the UI locally (npm run tauri build).
            2. This will overwrite the live Linux installer.
            3. Users will get this version on next install.
        `;

      if(window.confirm(confirmMsg)) {
          linuxBuildMutation.mutate({ version, changelog: notes });
      }
  };

  const handleWindowsRelease = (e) => {
      e.preventDefault();
      if(!version || !notes) return alert("Please fill all fields");

      const formData = new FormData();
      formData.append('version', version);
      formData.append('changelog', notes);
      if (exeFile) {
        formData.append('file', exeFile);
      }

      const confirmMsg = `
            Are you sure you want to publish Windows Release ${version}?

            1. Platform: Windows (x64 Installer)
            2. Binary: ${exeFile ? exeFile.name : 'Default Server Binary'}
            3. Endpoints will receive update notification.
        `;

      if(window.confirm(confirmMsg)) {
          windowsReleaseMutation.mutate(formData);
      }
  };

  const isProcessing = linuxBuildMutation.isLoading || windowsReleaseMutation.isLoading;

  return (
    <div className="space-y-6 animate-fade-in pb-10 relative">
      
      {isProcessing && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[9999] flex flex-col items-center justify-center transition-all duration-300 left-0 top-0 w-screen h-screen">
            <div className="bg-white p-10 rounded-3xl shadow-2xl flex flex-col items-center max-w-sm text-center border border-slate-200 animate-in zoom-in-95">
                <div className="relative mb-8">
                    <div className="w-20 h-20 border-[6px] border-indigo-100 rounded-full"></div>
                    <div className="w-20 h-20 border-[6px] border-indigo-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                    <Rocket className="w-8 h-8 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                </div>
                <h3 className="text-2xl font-black text-slate-800 mb-3">Deploying Release...</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-8">
                    Publishing release manifest, updating distribution nodes, and notifying agents.
                    <br/><span className="text-indigo-600 font-bold mt-2 block">Please wait, this may take a few seconds.</span>
                </p>
                <div className="bg-amber-50 text-amber-700 px-6 py-4 rounded-xl text-xs font-bold border border-amber-100 flex items-center w-full justify-center shadow-sm">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Do not close this window.
                </div>
            </div>
        </div>
      )}

      {/* HEADER */}
      <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center">
            <Rocket className="w-8 h-8 text-indigo-600 mr-3" /> Agent Release Manager
          </h1>
      </div>

      {/* PLATFORM SWITCHER TABS */}
      <div className="flex border-b border-slate-200 space-x-4 bg-white px-6 pt-4 rounded-t-2xl">
        <button
          onClick={() => setActiveTab('linux')}
          className={`pb-3 text-sm font-bold flex items-center border-b-2 transition-colors ${
            activeTab === 'linux'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <Terminal className="w-4 h-4 mr-2" /> Linux (Ubuntu / Debian)
        </button>
        <button
          onClick={() => setActiveTab('windows')}
          className={`pb-3 text-sm font-bold flex items-center border-b-2 transition-colors ${
            activeTab === 'windows'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <Monitor className="w-4 h-4 mr-2" /> Windows (x64 Installer)
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
         
         {/* BUILDER CARD: LINUX */}
         {activeTab === 'linux' && (
           <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm h-fit">
              <h3 className="font-bold text-lg mb-4 flex items-center text-slate-700">
                  <Package className="w-5 h-5 mr-2 text-indigo-500"/> Deploy Linux Release
              </h3>
              <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg mb-6 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="text-xs text-blue-800 leading-relaxed">
                      <strong>Pre-Requisite:</strong> Ensure you have compiled the UI locally (<code className="bg-blue-100 px-1 py-0.5 rounded">npm run tauri build</code>). The backend will package the Linux agent automatically.
                  </div>
              </div>
              <form onSubmit={handleLinuxRelease} className="space-y-4">
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Linux Version Tag</label>
                      <input 
                          className="w-full p-3 border rounded-lg font-mono focus:ring-2 focus:ring-indigo-500 outline-none" 
                          placeholder="v15.2.0" 
                          value={version}
                          onChange={e => setVersion(e.target.value)}
                      />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Linux Changelog</label>
                      <textarea 
                          className="w-full p-3 border rounded-lg h-32 focus:ring-2 focus:ring-indigo-500 outline-none" 
                          placeholder="• Fixed Linux eBPF hook&#10;• Updated security core" 
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                      />
                  </div>
                  <button 
                      type="submit"
                      disabled={linuxBuildMutation.isLoading}
                      className="w-full py-3 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 flex justify-center items-center transition-all active:scale-95"
                  >
                      <Rocket className="w-5 h-5 mr-2"/> Build & Release Linux
                  </button>
              </form>
           </div>
         )}

         {/* BUILDER CARD: WINDOWS */}
         {activeTab === 'windows' && (
           <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm h-fit">
              <h3 className="font-bold text-lg mb-4 flex items-center text-slate-700">
                  <Package className="w-5 h-5 mr-2 text-blue-500"/> Publish Windows Release
              </h3>
              <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg mb-6 flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="text-xs text-blue-800 leading-relaxed">
                      <strong>Windows Deployment:</strong> Upload your compiled Inno Setup installer (<code className="bg-blue-100 px-1 py-0.5 rounded">AIRS_Security_Windows_v1.0.exe</code>). The system will update all download links and prompt active endpoints.
                  </div>
              </div>
              <form onSubmit={handleWindowsRelease} className="space-y-4">
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Windows Version Tag</label>
                      <input 
                          className="w-full p-3 border rounded-lg font-mono focus:ring-2 focus:ring-blue-500 outline-none" 
                          placeholder="1.0.1" 
                          value={version}
                          onChange={e => setVersion(e.target.value)}
                      />
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Upload Installer Binary (.exe)</label>
                      <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors">
                              <div className="flex flex-col items-center justify-center pt-2 pb-2">
                                  <Upload className="w-6 h-6 text-slate-400 mb-1" />
                                  <p className="text-xs text-slate-500"><span className="font-bold">Click to upload</span> .exe installer</p>
                                  <p className="text-[10px] text-slate-400">{exeFile ? exeFile.name : 'AIRS_Security_Windows_v1.0.exe'}</p>
                              </div>
                              <input 
                                type="file" 
                                accept=".exe" 
                                className="hidden" 
                                onChange={e => setExeFile(e.target.files[0])} 
                              />
                          </label>
                      </div>
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Windows Changelog</label>
                      <textarea 
                          className="w-full p-3 border rounded-lg h-28 focus:ring-2 focus:ring-blue-500 outline-none" 
                          placeholder="• Hardened ETW memory tracing&#10;• Added zero-I/O IPC mapping" 
                          value={notes}
                          onChange={e => setNotes(e.target.value)}
                      />
                  </div>
                  <button 
                      type="submit"
                      disabled={windowsReleaseMutation.isLoading}
                      className="w-full py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20 flex justify-center items-center transition-all active:scale-95"
                  >
                      <Rocket className="w-5 h-5 mr-2"/> Publish Windows Release
                  </button>
              </form>
           </div>
         )}

         {/* ISOLATED CHANGELOG HISTORY */}
         <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="font-bold text-lg mb-4 flex items-center">
                <History className="w-5 h-5 mr-2 text-slate-500"/> 
                {activeTab === 'linux' ? 'Linux Release History' : 'Windows Release History'}
            </h3>
            <div className="space-y-6 relative border-l border-slate-200 ml-2 pl-6">
                {history.map((log, idx) => (
                    <div key={idx} className="relative">
                        <div className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-4 border-white shadow-sm ${activeTab === 'linux' ? 'bg-indigo-500' : 'bg-blue-500'}`}></div>
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-bold text-slate-800">{log.version}</span>
                            <span className="text-xs text-slate-400">
                                {log.release_date ? new Date(log.release_date).toLocaleDateString() : 'Recent'}
                            </span>
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">
                            {log.description || log.changelog}
                        </p>
                    </div>
                ))}
                {history.length === 0 && <p className="text-slate-400 text-sm italic">No releases published for this platform yet.</p>}
            </div>
         </div>

      </div>
    </div>
  );
};

export default ReleaseManagerPage;